/**
 * SDialog - Simple Dialog Component
 *
 * A minimal, clean wrapper around Material-UI's Dialog component with theme integration.
 * Designed as a lightweight alternative to BaseDialog without complex state management.
 *
 * Features:
 * - Full MUI Dialog API compatibility (all props passed through)
 * - Theme-integrated styling (background, border radius, shadow)
 * - Built-in title with close button (always visible)
 * - Optional action buttons (primary/secondary) with loading states
 * - Direct MUI DialogActions rendering for theme compatibility
 * - Minimal footprint with zero state management overhead
 *
 * @example
 * ```tsx
 * import { SDialog } from '@modules/ui-components';
 *
 * function MyComponent() {
 *   const [open, setOpen] = useState(false);
 *
 *   return (
 *     <SDialog
 *       open={open}
 *       onClose={() => setOpen(false)}
 *       title="Edit Project"
 *       maxWidth="sm"
 *       fullWidth
 *       primaryAction={{
 *         label: "Save Changes",
 *         onClick: handleSave,
 *       }}
 *       secondaryAction={{
 *         label: "Cancel",
 *         onClick: () => setOpen(false),
 *       }}
 *     >
 *       <DialogContent>Simple dialog content</DialogContent>
 *     </SDialog>
 *   );
 * }
 * ```
 */

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  CircularProgress,
  Box,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import type { SDialogProps } from './SDialog.types';

/**
 * SDialog Component
 *
 * A simple, theme-aware dialog wrapper that accepts all standard MUI Dialog props.
 * Automatically applies theme-based styling to the dialog paper component and includes
 * a built-in header with optional title and close button.
 *
 * @param props - All MUI DialogProps plus optional title and data-testid
 * @returns A themed Dialog component with built-in header
 */
export const SDialog = React.memo<SDialogProps>((props) => {
  const {
    children,
    title,
    onClose,
    actions,
    primaryAction,
    secondaryAction,
    showCloseButton = true,
    closeButtonAriaLabel = 'close dialog',
    contentFill = false,
    'data-testid': testId,
    ...restProps
  } = props;

  // Handle close button click
  const handleCloseClick = React.useCallback(() => {
    if (onClose) {
      onClose({}, 'backdropClick');
    }
  }, [onClose]);

  // Dev-mode guard: warn once if the caller is shipping a duplicate dismiss
  // affordance (X in the header AND a Cancel-labelled secondaryAction).
  // Production bundles strip this via dead-code elimination on the env check.
  if (process.env.NODE_ENV !== 'production') {
    const CANCEL_RE = /^(cancel|close|dismiss|отмена|закрыть)$/i;
    const looksCancel = !!(
      secondaryAction?.label && CANCEL_RE.test(secondaryAction.label.trim())
    );
    // eslint-disable-next-line react-hooks/rules-of-hooks
    React.useEffect(() => {
      if (looksCancel && showCloseButton) {
        // eslint-disable-next-line no-console
        console.warn(
          '[SDialog] Duplicate dismiss affordance detected. The close button' +
            ' (X) in the header and the "Cancel"-labelled secondaryAction do' +
            ' the same thing. Either remove the secondaryAction OR pass' +
            ' `showCloseButton={false}`.',
        );
      }
    }, [looksCancel, showCloseButton]);
  }

  // Render action button with loading state support
  const renderActionButton = (
    action: NonNullable<typeof primaryAction>,
    isPrimary: boolean,
    key?: string
  ) => {
    const {
      label,
      onClick,
      variant = isPrimary ? 'contained' : 'outlined',
      color = 'primary',
      disabled = false,
      loading = false,
      startIcon,
      endIcon,
      sx,
      'data-testid': buttonTestId,
      'aria-label': ariaLabel,
      'aria-disabled': ariaDisabled,
      'aria-describedby': ariaDescribedBy,
      title,
    } = action;

    const actualStartIcon = loading ? (
      <CircularProgress
        size={16}
        sx={{ color: 'inherit' }}
        data-testid={`${buttonTestId || 'action-button'}-loading`}
      />
    ) : startIcon;

    return (
      <Button
        key={key || label}
        variant={variant}
        color={color}
        disabled={disabled || loading}
        onClick={onClick}
        startIcon={actualStartIcon}
        endIcon={!loading ? endIcon : undefined}
        data-testid={buttonTestId}
        aria-label={ariaLabel}
        aria-disabled={ariaDisabled}
        aria-describedby={ariaDescribedBy}
        title={title}
        sx={{
          whiteSpace: 'nowrap',
          ...sx,
        }}
      >
        {label}
      </Button>
    );
  };

  return (
    <Dialog
      data-testid={testId}
      onClose={onClose}
      {...restProps}
    >
      {/* iOS navigation bar layout: close LEFT, title CENTER, phantom
          RIGHT slot for symmetric balance. CSS Grid (not flex) keeps the
          title centred even when the right slot is empty. */}
      <DialogTitle
        sx={{
          display: 'grid',
          gridTemplateColumns: '40px 1fr 40px',
          alignItems: 'center',
          gap: 1,
        }}
      >
        {/* LEFT cell: close button, or empty placeholder for balance. */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
          {showCloseButton && (
            <IconButton
              onClick={handleCloseClick}
              size="small"
              aria-label={closeButtonAriaLabel}
              sx={{
                padding: '4px',
                backgroundColor: (theme) => theme.palette.action.hover,
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Box>

        {/* CENTRE cell: title. Truncates with ellipsis for long strings so
            the centring invariant holds. */}
        <Box
          component="span"
          sx={{
            textAlign: 'center',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
          }}
        >
          {title}
        </Box>

        {/* RIGHT cell: phantom, empty. Reserved to keep the title centred
            symmetrically with the close-button cell on the left. */}
        <Box />
      </DialogTitle>
      <DialogContent
        sx={{
          padding: '0',
        }}
      >
        <Box
          sx={{
            padding: '16px 0',
            // `contentFill`: the box takes exactly the scroller's height (the
            // DialogContent is a flex item of the paper, so its height is
            // definite and the percentage resolves) and lays its children out
            // as a flex column — a child may then fill it and bound its own
            // scroll box. Off by default: a plain block that grows with its
            // children, exactly as shipped.
            ...(contentFill
              ? { display: 'flex', flexDirection: 'column', height: '100%', boxSizing: 'border-box' }
              : {}),
          }}
        >
          {children}
        </Box>
      </DialogContent>
      {(actions?.length || primaryAction || secondaryAction) && (
        <DialogActions sx={{ p: "16px 0 0", justifyContent: "space-between" }}>
          {/* Left-aligned actions */}
          {actions && actions.length > 0 && (
            <Box sx={{ display: "flex", flex: 1, gap: 1 }}>
              {actions.map((action, index) =>
                renderActionButton(action, false, `action-${index}`)
              )}
            </Box>
          )}

          {/* Right-aligned primary/secondary actions */}
          {(primaryAction || secondaryAction) && (
            <Box sx={{ display: "flex", flex: 1, justifyContent: 'flex-end', gap: 1 }}>
              {secondaryAction && renderActionButton(secondaryAction, false)}
              {primaryAction && renderActionButton(primaryAction, true)}
            </Box>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
});

SDialog.displayName = 'SDialog';

export default SDialog;
